// NCLT backend API helpers. Centralizes the base URL and gives every caller a
// single switch to enable payload encryption against the gateway.
//
// Envelope format (matches nclt-backend/Encryption.md):
//   POST: body = { key, iv, data } (base64)
//   GET:  query string = ?key=...&iv=...&data=...
//   Encrypted responses come back as { iv, data } with X-Payload-Encrypted: 1.
//
// Helpers return a Response-like { ok, status, body } so call sites can keep
// their existing `if (!res.ok) throw ...` checks with a one-line rename.

import {
  getPublicKey,
  encryptRequest,
  decryptResponse,
  isEncryptedResponse,
} from "../utils/ncltCrypto";
import { NCLT_API_BASE_URL } from "../config/ncltApiConfig";

export { NCLT_API_BASE_URL };

// Backend at NCLT_API_BASE_URL must serve `GET /api/public-key` (returns RSA PEM)
// and the payload-encryption middleware must be live. Mirror to `false` if you
// ever roll back to a build without the middleware.
const ENCRYPTION_ENABLED = true;

const JSON_HEADERS = { "Content-Type": "application/json" };

function headersToObject(headers) {
  const out = {};
  if (!headers || typeof headers.forEach !== "function") return out;
  headers.forEach((value, key) => {
    out[key.toLowerCase()] = value;
  });
  return out;
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

async function unwrapBody(response, aesKey) {
  const headers = headersToObject(response.headers);
  const body = await parseJsonSafe(response);
  if (aesKey && body && isEncryptedResponse(headers, body)) {
    return await decryptResponse(body, aesKey);
  }
  return body;
}

export async function ncltPost(path, data) {
  const url = `${NCLT_API_BASE_URL}${path}`;

  if (!ENCRYPTION_ENABLED) {
    const response = await fetch(url, {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify(data ?? {}),
    });
    const body = await parseJsonSafe(response);
    return { ok: response.ok, status: response.status, body };
  }

  const publicKey = await getPublicKey(NCLT_API_BASE_URL);
  const { envelope, aesKey } = await encryptRequest(data, publicKey);
  const response = await fetch(url, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(envelope),
  });
  const body = await unwrapBody(response, aesKey);
  return { ok: response.ok, status: response.status, body };
}

export async function ncltGet(path, params) {
  const search = new URLSearchParams();

  if (ENCRYPTION_ENABLED) {
    const publicKey = await getPublicKey(NCLT_API_BASE_URL);
    const { envelope, aesKey } = await encryptRequest(params ?? {}, publicKey);
    search.set("key", envelope.key);
    search.set("iv", envelope.iv);
    search.set("data", envelope.data);
    const response = await fetch(
      `${NCLT_API_BASE_URL}${path}?${search.toString()}`
    );
    const body = await unwrapBody(response, aesKey);
    return { ok: response.ok, status: response.status, body };
  }

  if (params && typeof params === "object") {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) search.set(k, String(v));
    }
  }
  const qs = search.toString();
  const url = qs
    ? `${NCLT_API_BASE_URL}${path}?${qs}`
    : `${NCLT_API_BASE_URL}${path}`;
  const response = await fetch(url);
  const body = await parseJsonSafe(response);
  return { ok: response.ok, status: response.status, body };
}
